import React, { useEffect, useMemo, useRef, useState } from 'react';
import { InputNumber, Segmented, Switch } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { useAuth } from '../../../providers/AuthProvider';
import User from '../../../models/User';
import AppButton from '../../../components/AppButton';
import { CommitmentOptions } from './DashboardPreferences';

/**
 * Redesigned work preferences page.
 * Same data + rules as WorkPreferences / PreferenceQuestionnaire / PreferenceRatings, but both
 * sections (work preferences + assignment ratings) live on one page and save in a single request.
 */

const SHADOW = 'shadow-[0_1px_3px_rgba(16,24,40,0.06),0_1px_2px_rgba(16,24,40,0.04)]';

const SEGMENTED_CLASSES = [
    'rounded-lg bg-gray-100 p-1',
    '[&_.ant-segmented-item]:rounded-md',
    '[&_.ant-segmented-item-label]:min-h-[32px] [&_.ant-segmented-item-label]:px-4 [&_.ant-segmented-item-label]:text-xs [&_.ant-segmented-item-label]:font-semibold [&_.ant-segmented-item-label]:leading-[32px] [&_.ant-segmented-item-label]:text-gray-500',
    '[&_.ant-segmented-item-selected_.ant-segmented-item-label]:text-primary',
].join(' ');

const RATING_SEGMENTED_CLASSES = `${SEGMENTED_CLASSES} [&_.ant-segmented-item-label]:px-0 [&_.ant-segmented-item-label]:w-10 sm:[&_.ant-segmented-item-label]:w-11`;

const COMMITMENT_COPY = {
    1: { title: 'Side Hustler', description: 'Employed and looking for part-time freelance work for extra income' },
    3: { title: 'Full-Time Freelancer', description: 'Freelance/contract work is my primary source of income' },
};

const MIN_HOURS = 1;
const MAX_HOURS = 40;

const US_TIME_ZONES = () => TB.TimeZonesInUS();
const NON_US_TIME_ZONES = () => TB.TimeZonesNotInUS();

// User.GroupedTalentWorkPreferences → [{ label, items: [{ key, label, subLabel }] }]
const RATING_SECTIONS = TB.flatten(TB.map(User.GroupedTalentWorkPreferences, group => (
    TB.map(group, (items, label) => ({ label: TB.trimEnd(label, ':'), items }))
)));

const RATING_OPTIONS = TB.range(1, 6).map(value => ({ label: String(value), value }));

// Flags may arrive as booleans or 0/1 — normalize to true / false / undefined
const toBool = (value) => {
    if (value === true || value === 1 || value === '1') return true;
    if (value === false || value === 0 || value === '0') return false;
    return undefined;
};

const yesNoValue = (value) => {
    const bool = toBool(value);
    return bool === true ? 'yes' : bool === false ? 'no' : undefined;
};

/* ----------------------------------------------------------------------------
 * Time zone helpers (same rules as PreferenceQuestionnaire.mapping)
 * ------------------------------------------------------------------------- */

const isUsZoneSelected = (preferences, zone) => !!preferences.prefers_any_timezone || !!preferences.selected_time_zones?.[zone.id];
const isGlobalSelected = preferences => !!preferences.prefers_any_timezone || !!preferences.prefers_any_non_us_timezone;

// Rebuild the derived time zone fields after a change to the selection
const withTimeZones = (preferences, usSelected, globalSelected) => {
    const selected_time_zones = {};
    US_TIME_ZONES().forEach(zone => { selected_time_zones[zone.id] = !!usSelected[zone.id]; });
    NON_US_TIME_ZONES().forEach(zone => { selected_time_zones[zone.value ?? zone.id] = !!globalSelected; });

    const allUs = US_TIME_ZONES().every(zone => usSelected[zone.id]);

    return {
        ...preferences,
        selected_time_zones,
        prefers_any_non_us_timezone: !!globalSelected,
        prefers_any_timezone: allUs && !!globalSelected,
    };
};

const usSelectionFor = preferences => TB.reduce(US_TIME_ZONES(), (result, zone) => {
    result[zone.id] = isUsZoneSelected(preferences, zone);
    return result;
}, {});

/* ----------------------------------------------------------------------------
 * Validation (same rules as the old Formsy form)
 * ------------------------------------------------------------------------- */

const validatePreferences = (preferences) => {
    const errors = {};
    const hours = preferences.available_hours_per_week;

    if (!TB.includes([1, 3], Number(preferences.talent_commitment_level))) {
        errors.talent_commitment_level = 'Select your current work status.';
    }
    if (!TB.isBoolean(toBool(preferences.has_full_time_employment_interest))) {
        errors.has_full_time_employment_interest = 'Let us know if you’re open to permanent roles.';
    }
    if (!Number.isInteger(Number(hours)) || hours === null || hours === '' || Number(hours) < MIN_HOURS || Number(hours) > MAX_HOURS) {
        errors.available_hours_per_week = `The number of hours must be between ${MIN_HOURS} and ${MAX_HOURS}`;
    }
    if (!TB.isBoolean(toBool(preferences.can_travel_for_work))) {
        errors.can_travel_for_work = 'Let us know if you’re willing to work on site.';
    }

    return errors;
};

// Same payload as WorkPreferences.submitPreferences()
const formatPreferences = (preferences) => {
    const fields = [
        'can_travel_for_work',
        'talent_commitment_level',
        'available_hours_per_week',
        'has_full_time_employment_interest',
        'is_available',
    ];

    if (preferences.can_travel_for_work) {
        fields.push('max_travel_distance', 'travel_days_per_week', 'max_travel_distance_units');
    }
    fields.push('prefers_any_non_us_timezone', 'prefers_any_timezone');

    const talent_work_preferences = TB.reduce(User.TalentWorkPreferences, (result, rating) => {
        result[rating] = parseInt(preferences.talent_work_preferences?.[rating]) || 1;
        return result;
    }, {});

    const time_zones = [];
    for (const timeZone of Object.values({ ...laravel.talent_questions.time_zones })) {
        if (preferences.prefers_any_timezone || preferences.selected_time_zones?.[timeZone.id]) {
            time_zones.push(timeZone.id);
        }
    }

    const formatted = {
        ...TB.pick(preferences, fields),
        time_zones,
        talent_work_preferences,
    };
    formatted.can_travel_for_work = formatted.can_travel_for_work ? 1 : 0;
    formatted.has_full_time_employment_interest = formatted.has_full_time_employment_interest ? 1 : 0;
    formatted.available_hours_per_week = parseInt(formatted.available_hours_per_week);

    return formatted;
};

/* ----------------------------------------------------------------------------
 * Layout pieces
 * ------------------------------------------------------------------------- */

const Card = ({ title, description, children, footer }) => (
    <section className={`overflow-hidden rounded-2xl bg-white ${SHADOW}`}>
        {title && (
            <header className="border-0 border-b border-solid border-gray-100 px-4 py-5 sm:px-8">
                <h2 className="m-0 text-lg font-semibold text-gray-900">{title}</h2>
                {description && <p className="m-0 mt-1 text-sm text-gray-500">{description}</p>}
            </header>
        )}
        <div className="divide-y divide-gray-100">{children}</div>
        {footer}
    </section>
);

const Row = React.forwardRef(({ label, description, error, children }, ref) => (
    <div ref={ref} className="grid grid-cols-1 gap-3 px-4 py-5 sm:px-8 sm:py-6 lg:grid-cols-[minmax(0,300px)_minmax(0,1fr)] lg:gap-8">
        <div>
            <p className="m-0 text-sm font-semibold text-gray-900">{label}</p>
            {description && <p className="m-0 mt-1 text-xs leading-relaxed text-gray-500">{description}</p>}
        </div>
        <div className="min-w-0">
            {children}
            {error && (
                <p className="m-0 mt-2 text-sm text-danger" role="alert">{error}</p>
            )}
        </div>
    </div>
));

const CommitmentCard = ({ level, checked, disabled, onSelect }) => {
    const copy = COMMITMENT_COPY[level] || { title: CommitmentOptions[level]?.title };

    return (
        <button
            type="button"
            role="radio"
            aria-checked={checked}
            disabled={disabled}
            onClick={() => onSelect(level)}
            className={TB.mapClass('relative flex w-full flex-col items-start gap-1 rounded-xl border-2 border-solid bg-white p-4 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 sm:p-5', {
                'border-primary-lighter bg-primary/5 ring-4 ring-primary/10': checked,
                'border-gray-200 hover:border-gray-300': !checked,
            })}
        >
            <span className="pr-8 text-sm font-semibold text-gray-900">{copy.title}</span>
            {copy.description && <span className="text-xs leading-relaxed text-gray-500">{copy.description}</span>}
            {checked && (
                <span className="absolute right-4 top-4 flex h-6 w-6 items-center justify-center rounded-full bg-primary-lighter text-white" aria-hidden="true">
                    <CheckOutlined className="text-xs" />
                </span>
            )}
        </button>
    );
};

const ZoneChip = ({ label, selected, disabled, onClick }) => (
    <button
        type="button"
        aria-pressed={selected}
        disabled={disabled}
        onClick={onClick}
        className={TB.mapClass('inline-flex items-center gap-1.5 rounded-full border border-solid px-3.5 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60', {
            'border-primary-lighter/60 bg-white text-primary hover:bg-primary/5': selected,
            'border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:text-gray-700': !selected,
        })}
    >
        {selected && <CheckOutlined className="text-xs" aria-hidden="true" />}
        {label}
    </button>
);

const SummaryRow = ({ label, value }) => (
    <div className="flex items-center justify-between gap-4 py-3">
        <span className="text-sm text-gray-700">{label}</span>
        <span className="text-sm font-semibold text-primary">{value}</span>
    </div>
);

/* ----------------------------------------------------------------------------
 * Page
 * ------------------------------------------------------------------------- */

const WorkPreferencesForm = () => {
    const { auth, talentUpdate } = useAuth();

    const [savedPreferences, setSavedPreferences] = useState(() => auth?.talentPreferences());
    const [preferences, setPreferences] = useState(savedPreferences);
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [matchingJobs, setMatchingJobs] = useState(null);

    const rowRefs = useRef({});

    // "Matching jobs today" for the sidebar (hidden if stats aren't available)
    useEffect(() => {
        let isMounted = true;
        auth?.getTalentUserStats?.()
            .then(stats => {
                if (isMounted && stats && TB.isDefined(stats.total_matching_open_positions)) {
                    setMatchingJobs(stats.total_matching_open_positions);
                }
            })
            .catch(console.warn);
        return () => { isMounted = false; };
    }, []);

    const isDirty = !TB.isEqual(preferences, savedPreferences);

    const update = (changes) => {
        setPreferences(current => ({ ...current, ...changes }));
        setErrors(current => TB.omit(current, TB.keys(changes)));
    };

    /* ---------- field handlers ---------- */

    const onToggleAvailable = async (checked) => {
        if (!checked && preferences.is_available) {
            const confirm = await TB.sysAlert('talent.turn_off_availability_warning');
            if (!confirm) return;
        }
        update({ is_available: checked });
    };

    const usSelected = usSelectionFor(preferences);
    const globalSelected = isGlobalSelected(preferences);
    const allZonesSelected = !!preferences.prefers_any_timezone;
    const selectedZoneCount = TB.filter(TB.values(usSelected)).length + (globalSelected ? 1 : 0);

    const toggleUsZone = zone => setPreferences(current => withTimeZones(
        current,
        { ...usSelectionFor(current), [zone.id]: !isUsZoneSelected(current, zone) },
        isGlobalSelected(current)
    ));
    const toggleGlobal = () => setPreferences(current => withTimeZones(current, usSelectionFor(current), !isGlobalSelected(current)));
    const selectAllZones = () => setPreferences(current => withTimeZones(
        current,
        TB.reduce(US_TIME_ZONES(), (result, zone) => ({ ...result, [zone.id]: true }), {}),
        true
    ));
    const clearAllZones = () => setPreferences(current => withTimeZones(current, {}, false));

    const setRating = (key, value) => setPreferences(current => ({
        ...current,
        talent_work_preferences: { ...current.talent_work_preferences, [key]: value },
    }));

    /* ---------- save / cancel ---------- */

    const onCancel = () => {
        setPreferences(savedPreferences);
        setErrors({});
    };

    const onSave = async () => {
        const validationErrors = validatePreferences(preferences);
        if (!TB.isEmpty(validationErrors)) {
            setErrors(validationErrors);
            const firstInvalid = rowRefs.current[TB.first(TB.keys(validationErrors))];
            firstInvalid?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
            return;
        }

        setIsSaving(true);
        try {
            const updatedAuth = await talentUpdate(formatPreferences(preferences)).catch(console.warn);

            if (updatedAuth) {
                const next = updatedAuth.talentPreferences();
                setSavedPreferences(next);
                setPreferences(next);
                TB.sysAlert('talent.work_preferences_updated');
            } else {
                TB.showServerErrorToast();
            }
        } catch (e) {
            console.warn(e);
            TB.showServerErrorToast();
        } finally {
            setIsSaving(false);
        }
    };

    if (!preferences) return null;

    const disabled = isSaving;
    const commitmentLevel = Number(preferences.talent_commitment_level);
    const showGraphic = TB.platformHasImage('Asset_Talent_Work_Preferences_Graphic');

    return (
        <div className="mx-auto w-full max-w-[1450px] px-4 pb-4 pt-6 sm:px-6 md:pt-10">
            <header className="mb-5 md:mb-7">
                <h1 className="m-0 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">Work preferences</h1>
                <p className="m-0 mt-1 text-sm text-gray-500 sm:text-base">
                    Tell us how and when you want to work so your matches fit.
                </p>
            </header>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
                <div className="flex min-w-0 flex-col gap-6">
                    {/* Card 1 — how & when */}
                    <Card title="Availability" description="How and when you can take on work.">
                        <Row label="Open to Work" description="Turn off to pause your profile. Clients won’t see you or invite you.">
                            <div className="flex flex-wrap items-center gap-3">
                                <Switch
                                    checked={!!preferences.is_available}
                                    onChange={onToggleAvailable}
                                    disabled={disabled}
                                    className={preferences.is_available ? 'bg-[#3E8E5E]' : 'bg-gray-300'}
                                />
                                <span className="text-xs text-gray-500">
                                    {preferences.is_available ? (
                                        <><span className="font-semibold text-[#3E8E5E]">Live</span> · Clients can find you and invite you to projects</>
                                    ) : (
                                        <><span className="font-semibold text-gray-700">Paused</span> · Your profile won’t show up on the {laravel.platform_name} platform</>
                                    )}
                                </span>
                            </div>
                        </Row>

                        <Row
                            ref={el => { rowRefs.current.talent_commitment_level = el; }}
                            label="Current work status"
                            description="Helps clients understand your capacity."
                            error={errors.talent_commitment_level}
                        >
                            <div role="radiogroup" aria-label="Current work status" className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                                {TB.keys(CommitmentOptions).map(key => (
                                    <CommitmentCard
                                        key={key}
                                        level={Number(key)}
                                        checked={commitmentLevel === Number(key)}
                                        disabled={disabled}
                                        onSelect={level => update({ talent_commitment_level: level })}
                                    />
                                ))}
                            </div>
                        </Row>

                        <Row
                            ref={el => { rowRefs.current.has_full_time_employment_interest = el; }}
                            label="Open to permanent roles?"
                            description="Including contract-to-hire."
                            error={errors.has_full_time_employment_interest}
                        >
                            <Segmented
                                className={SEGMENTED_CLASSES}
                                options={[{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }]}
                                value={yesNoValue(preferences.has_full_time_employment_interest)}
                                disabled={disabled}
                                onChange={value => update({ has_full_time_employment_interest: value === 'yes' })}
                            />
                        </Row>

                        <Row
                            ref={el => { rowRefs.current.available_hours_per_week = el; }}
                            label="Hours available"
                            description="What you can typically take on each week."
                            error={errors.available_hours_per_week}
                        >
                            <InputNumber
                                aria-label="Hours available per week"
                                controls={false}
                                min={MIN_HOURS}
                                max={MAX_HOURS}
                                precision={0}
                                inputMode="numeric"
                                value={preferences.available_hours_per_week}
                                onChange={value => update({ available_hours_per_week: value })}
                                disabled={disabled}
                                status={errors.available_hours_per_week ? 'error' : undefined}
                                addonAfter={<span className="text-xs text-gray-500">hours / week</span>}
                                className="max-w-[12rem] w-full [&_.ant-input-number-group-addon]:bg-gray-50 [&_input]:h-11 [&_input]:text-base [&_input]:font-semibold"
                            />
                        </Row>

                        <Row
                            ref={el => { rowRefs.current.can_travel_for_work = el; }}
                            label="Onsite client work?"
                            description="Willing to work at a client’s location."
                            error={errors.can_travel_for_work}
                        >
                            <Segmented
                                className={SEGMENTED_CLASSES}
                                options={[{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }]}
                                value={yesNoValue(preferences.can_travel_for_work)}
                                disabled={disabled}
                                onChange={value => update({ can_travel_for_work: value === 'yes' })}
                            />
                        </Row>

                        <Row label="Time zones" description="Where you’re willing to work.">
                            <div className="flex flex-wrap gap-2">
                                {US_TIME_ZONES().map(zone => (
                                    <ZoneChip
                                        key={zone.id}
                                        label={zone.name}
                                        selected={usSelected[zone.id]}
                                        disabled={disabled}
                                        onClick={() => toggleUsZone(zone)}
                                    />
                                ))}
                                <ZoneChip
                                    label="Global (outside U.S.)"
                                    selected={globalSelected}
                                    disabled={disabled}
                                    onClick={toggleGlobal}
                                />
                            </div>
                            <div className="mt-3 flex items-center gap-4">
                                {!allZonesSelected && (
                                    <button type="button" disabled={disabled} onClick={selectAllZones} className="border-0 bg-transparent p-0 text-xs font-semibold text-primary hover:text-primary-dark">
                                        Select all
                                    </button>
                                )}
                                {selectedZoneCount > 0 && (
                                    <button type="button" disabled={disabled} onClick={clearAllZones} className="border-0 bg-transparent p-0 text-xs font-semibold text-primary hover:text-primary-dark">
                                        Clear all
                                    </button>
                                )}
                            </div>
                        </Row>
                    </Card>

                    {/* Card 2 — assignment ratings (previously step 2) */}
                    <Card
                        title="Assignment types & length"
                        description="Rate your interest in each from 1 (not interested) to 5 (very interested)."
                    >
                        {RATING_SECTIONS.map(section => (
                            <div key={section.label} className="px-4 py-5 sm:px-8 sm:py-6">
                                <p className="m-0 mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-gray-400">{section.label}</p>
                                <div className="flex flex-col gap-4">
                                    {section.items.map(item => (
                                        <div key={item.key} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
                                            <div className="min-w-0">
                                                <p className="m-0 text-sm font-medium text-gray-900">{item.label}</p>
                                                {item.subLabel && <p className="m-0 mt-0.5 text-xs text-gray-500">{item.subLabel}</p>}
                                            </div>
                                            <Segmented
                                                aria-label={`Interest in ${item.label}`}
                                                className={`${RATING_SEGMENTED_CLASSES} self-start sm:self-auto`}
                                                options={RATING_OPTIONS}
                                                value={parseInt(preferences.talent_work_preferences?.[item.key]) || 1}
                                                disabled={disabled}
                                                onChange={value => setRating(item.key, value)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </Card>

                    {/* Single save for both cards — sticky so it's always reachable on mobile */}
                    <div className={`sticky bottom-0 z-20 -mx-4 flex flex-col gap-3 border-0 border-t border-solid border-gray-200 bg-white/95 px-4 py-3 backdrop-blur sm:mx-0 sm:flex-row sm:items-center sm:justify-between sm:rounded-2xl sm:border-t-0 sm:px-6 sm:py-4 ${SHADOW}`}>
                        <p className="m-0 text-sm text-gray-500" aria-live="polite">
                            {isDirty ? (
                                <><span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-amber-500 align-middle" aria-hidden="true" />You have unsaved changes</>
                            ) : 'All changes saved'}
                        </p>
                        <div className="flex gap-2">
                            <AppButton variant="neutral" size="lg" disabled={disabled || !isDirty} onClick={onCancel} className="flex-1 sm:flex-none">
                                Cancel
                            </AppButton>
                            <AppButton size="lg" loading={isSaving} disabled={!isDirty} onClick={onSave} className="flex-1 sm:flex-none">
                                Save preferences
                            </AppButton>
                        </div>
                    </div>
                </div>

                {/* Sidebar */}
                <aside className="flex flex-col gap-6 lg:sticky lg:top-6">
                    <div className={`rounded-2xl border-0 border-l-4 border-solid border-primary-lighter bg-white p-5 sm:p-6 ${SHADOW}`}>
                        <h2 className="m-0 text-lg font-semibold text-gray-900">How we use this</h2>
                        <p className="m-0 mt-2 text-sm leading-relaxed text-gray-500">
                            Your preferences shape which jobs show as matches and which clients see you first.
                        </p>
                        <div className="mt-3 divide-y divide-gray-100 border-0 border-t border-solid border-gray-100">
                            {matchingJobs !== null && <SummaryRow label="Matching jobs today" value={matchingJobs} />}
                            <SummaryRow
                                label="Time zones selected"
                                value={allZonesSelected ? 'All' : (selectedZoneCount || 'None')}
                            />
                            <SummaryRow label="Hours / week" value={preferences.available_hours_per_week || '—'} />
                        </div>
                    </div>

                    {showGraphic && (
                        <img
                            src={laravel.platform_settings.Asset_Talent_Work_Preferences_Graphic}
                            alt=""
                            className="hidden w-full rounded-2xl lg:block"
                        />
                    )}
                </aside>
            </div>
        </div>
    );
};

export default WorkPreferencesForm;
